// src/App.js (or src/components/App.js depending on your project)
import "./components/App.css";
import React from "react";
import { useContext, useEffect } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  useLocation,
} from "react-router-dom";

import Home from "./components/Home";
import Dashboard from "./components/Dashboard";
import Login from "./components/Login";
import Signup from "./components/Signup";
import EditMap from "./components/EditMap";
import YourMaps from "./components/YourMaps";
import DataIntegration from "./components/DataIntergration";
import ProfileSettings from "./components/ProfileSettings";
import ProfilePage from "./components/ProfilePage";
import StarredMaps from "./components/StarredMaps";
import NotificationList from "./components/NotificationList";
import ChangePassword from "./components/ChangePassword";
import DeleteAccount from "./components/DeleteAccount";
import DocsLayout, { DocsLanding, DocPage } from "./components/Docs";
import AdminPanel from "./components/AdminPanel";
import BannedUser from "./components/BannedUser";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Terms from "./components/Terms";
import RefundPolicy from "./components/RefundPolicy";
import VerifyAccount from "./components/VerifyAccount";
import Verified from "./components/Verified";
import VerificationError from "./components/VerificationError";
import PublicExplore from "./components/PublicExplore";
import PublicDataIntegration from "./components/PublicDataIntegration";
import PublicLayout from "./components/PublicLayout";
import LoggedInExplore from "./components/LoggedInExplore";
import PublicMapDetail from "./components/PublicMapDetail";
import LoggedInMapDetail from "./components/LoggedInMapDetail";
import Map from "./components/Map";
import Explore from "./components/Explore";
import ResetPasswordPage from "./components/ResetPassword";
import PrivateRoute from "./components/PrivateRoute";
import MapEmbed from "./components/MapEmbed";
import NotFound from "./components/NotFound";
import FAQ from "./components/FAQ";
import ProPage from "./components/ProPage";

import { UserContext } from "./context/UserContext";
import { SidebarContext } from "./context/SidebarContext";

const DEFAULT_TITLE = "Map in Color";

const ROUTE_TITLES = {
  "/": "Map in Color",
  "/explore": "Explore",
  "/dashboard": "Dashboard",
  "/create": "Create a new map",
  "/your-maps": "Your Maps",
  "/starred-maps": "Starred Maps",
  "/login": "Login",
  "/signup": "Signup",
  "/settings": "Settings",
  "/notifications": "Notifications",
  "/change-password": "Change Password",
  "/delete-account": "Delete Account",
  "/faq": "FAQ",
  "/pro": "Pro",
  "/playground": "Playground",
  "/privacy": "Privacy Policy",
  "/terms": "Terms",
  "/refund": "Refund Policy",
  "/verify-account": "Verify Account",
  "/verified": "Verified",
  "/verification-error": "Verification Error",
  "/reset-password": "Reset Password",
  "/adminPanel": "Admin",
  "/banned": "Banned",
  "/map": "Map",
  "/explore-old": "Explore",
};

function getPageTitle(pathname) {
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];
  if (pathname.startsWith("/map/")) return "Map";
  if (pathname.startsWith("/embed/")) return "Map";
  if (pathname.startsWith("/edit/")) return "Edit Map";
  const profileMatch = pathname.match(/^\/profile\/([^/]+)/);
  if (profileMatch) {
    const username = decodeURIComponent(profileMatch[1]);
    return `${username}'s profile`;
  }
  if (pathname.startsWith("/docs")) return pathname === "/docs" ? "Docs" : "Docs";
  return DEFAULT_TITLE;
}

/* Scroll to top and set document title on route change */
function ScrollToTopWrapper() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  useEffect(() => {
    document.title = getPageTitle(pathname);
  }, [pathname]);
  return <Outlet />;
}

function SidebarAutoCloseOnRouteChange() {
  const { pathname } = useLocation();
  const { setIsCollapsed } = useContext(SidebarContext);
  const prevPathRef = React.useRef(pathname);

  useEffect(() => {
    if (prevPathRef.current === pathname) return;
    prevPathRef.current = pathname;

    const isMobile = window.innerWidth < 1000;
    if (isMobile) setIsCollapsed(true);
  }, [pathname, setIsCollapsed]);

  return <Outlet />;
}

export default function App() {
  const { authToken, profile } = useContext(UserContext);
  const isUserLoggedIn = !!authToken && !!profile;

  const router = createBrowserRouter([
    {
      element: <SidebarAutoCloseOnRouteChange />,
      children: [
        {
          element: <ScrollToTopWrapper />,
          children: [
            {
              path: "/",
              element: <PublicLayout />,
              children: [
                { index: true, element: <Home /> },
                { path: "playground", element: <PublicDataIntegration /> },
                { path: "pro", element: <ProPage /> },
              ],
            },
            {
              path: "/docs",
              element: <DocsLayout />,
              children: [
                { index: true, element: <DocsLanding /> },
                { path: ":topic", element: <DocPage /> },
              ],
            },
            { path: "/faq", element: <FAQ /> },

            {
              path: "/create",
              element: (
                <PrivateRoute>
                  <DataIntegration />
                </PrivateRoute>
              ),
            },

            {
              path: "/dashboard",
              element: (
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              ),
            },

            {
              path: "/map/:id",
              element: isUserLoggedIn ? <LoggedInMapDetail /> : <PublicMapDetail />,
            },

            {
              path: "/embed/:id",
              element: <MapEmbed />,
            },


            {
              path: "/explore",
              element: isUserLoggedIn ? <LoggedInExplore /> : <PublicExplore />,
            },

            {
              path: "/your-maps",
              element: (
                <PrivateRoute>
                  <YourMaps />
                </PrivateRoute>
              ),
            },

            {
              path: "/starred-maps",
              element: (
                <PrivateRoute>
                  <StarredMaps />
                </PrivateRoute>
              ),
            },

            { path: "/login", element: <Login /> },
            { path: "/signup", element: <Signup /> },

            {
              path: "/edit/:mapId",
              element: (
                <PrivateRoute>
                  <EditMap />
                </PrivateRoute>
              ),
            },

            {
              path: "/profile/:username",
              element: (
                <PrivateRoute>
                  <ProfilePage />
                </PrivateRoute>
              ),
            },

            {
              path: "/settings",
              element: (
                <PrivateRoute>
                  <ProfileSettings />
                </PrivateRoute>
              ),
            },

            {
              path: "/change-password",
              element: (
                <PrivateRoute>
                  <ChangePassword />
                </PrivateRoute>
              ),
            },

            {
              path: "/delete-account",
              element: (
                <PrivateRoute>
                  <DeleteAccount />
                </PrivateRoute>
              ),
            },

            {
              path: "/notifications",
              element: (
                <PrivateRoute>
                  <NotificationList />
                </PrivateRoute>
              ),
            },

            { path: "/adminPanel", element: <AdminPanel /> },
            { path: "/banned", element: <BannedUser /> },
            { path: "/privacy", element: <PrivacyPolicy /> },
            { path: "/terms", element: <Terms /> },
            { path: "/refund", element: <RefundPolicy /> },
            { path: "/verify-account", element: <VerifyAccount /> },
            { path: "/verified", element: <Verified /> },
            { path: "/verification-error", element: <VerificationError /> },

            { path: "/map", element: <Map /> },

            // legacy
            { path: "/explore-old", element: <Explore /> },
            { path: "/reset-password", element: <ResetPasswordPage /> },

            { path: "*", element: <NotFound /> },
          ],
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
}
